import PropTypes from "prop-types"
import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  CardBody,
  Card,
  Container,
  Label,
  Form,
  FormFeedback,
  Input,
  Button,
  Spinner,
} from "reactstrap"
import { useTranslation } from "react-i18next"

// Redux
import { connect, useSelector, useDispatch } from "react-redux"
import { Link, useNavigate } from "react-router-dom"

// Formik validation
import * as Yup from "yup"
import { useFormik } from "formik"

import { registerClub } from "store/auth/register/registerSlice"

const ClubRegister = () => {
  const navigate = useNavigate()
  const { error, loading } = useSelector((state) => state.Register)

  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [otherRole, setOtherRole] = useState("")
  const handleSourceChange = (e) => {
    const value = e.target.value
    validation.handleChange(e) // Ensure formik handles this value change
  }

  const SourceEnum = {
    WEBSITE: "Website",
    SOCIAL_MEDIA: "Social Media",
    APP_USER: "User",
  }

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      source: SourceEnum.WEBSITE,
      managerRole: "President",
      nameFr: "",
      nameAr: "",
      city: "",
      category: "Amateur",
      description: "",
      managerName: "",
      managerEmail: "",
      managerRoleOther: "",
      managerPhoneNumber: "",
      password: "",
      confirmPassword: "",
      logo: null,
      otherRole: "",
    },

    validationSchema: Yup.object({
      nameFr: Yup.string().required(t("validation.nameFr")),
      nameAr: Yup.string().required(t("validation.nameAr")),
      // clubName: Yup.string().required(t("validation.clubName")),
      city: Yup.string().required(t("validation.city")),
      category: Yup.string().required(t("validation.category")),
      description: Yup.string().notRequired(),
      managerName: Yup.string().required(t("validation.managerName")),
      managerEmail: Yup.string()
        .email(t("validation.managerEmail.invalid"))
        .required(t("validation.managerEmail.required")),
      managerRole: Yup.string().required(t("validation.managerRole")),

      managerPhoneNumber: Yup.string()
        .matches(/^[0-9]+$/, t("validation.managerPhoneNumber.invalid"))
        .required(t("validation.managerPhoneNumber.required")),
      password: Yup.string()
        .min(6, t("validation.password.min"))
        .required(t("validation.password.required")),
      confirmPassword: Yup.string()
        .oneOf(
          [Yup.ref("password"), null],
          t("validation.confirmPassword.match")
        )
        .required(t("validation.confirmPassword.required")),
      source: Yup.string().required(t("validation.source")),
      logo: Yup.mixed()
        .required(t("validation.logo.required"))
        .test("fileType", t("validation.logo.fileType"), (value) =>
          value
            ? ["image/jpeg", "image/png", "image/gif"].includes(value.type)
            : false
        )
        .test("fileSize", t("validation.logo.fileSize"), (value) =>
          value ? value.size <= 2 * 1024 * 1024 : false
        ),
    }),

    onSubmit: (values) => {
      const formData = new FormData()

      // Ajout des champs texte
      formData.append("nameFr", values.nameFr)
      formData.append("nameAr", values.nameAr)
      formData.append("city", values.city)
      formData.append("category", values.category)
      formData.append("description", values.description || "")
      formData.append("managerName", values.managerName)
      formData.append("email", values.managerEmail)
      formData.append("managerRole", values.managerRole)
      formData.append("phone", values.managerPhoneNumber)
      formData.append("password", values.password)
      formData.append("confirmPassword", values.confirmPassword)
      // formData.append("source", values.source)

      // Ajout du fichier logo
      if (values.logo) {
        formData.append("logo", values.logo)
      }

      if (values.otherRole) {
        formData.append("managerRoleOther", values.otherRole)
      }

      dispatch(registerClub({ club: formData, history: navigate }))
    },
  })

  return (
    <Form
      className="mt-4"
      onSubmit={(e) => {
        e.preventDefault()
        validation.handleSubmit()
        return false
      }}
      action="#"
      autoComplete="off"
    >
      <Card className="mb-4">
        <CardBody>
          <h5
            style={{
              fontWeight: "bold", // Propriété correcte pour définir "bold"
              fontSize: "25px", // Ajout de l'unité "px"
              marginBottom: "30px",
            }}
          >
            {t("register.title")}
          </h5>

          <Row>
            {/* Première ligne : Name Fr et Name Ar */}

            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label" htmlFor="nameFr">
                  {t("register.nameFr")} *
                </Label>
                <Input
                  name="nameFr"
                  className="form-control"
                  placeholder={t("register.enterNameFr")}
                  type="text"
                  id="nameFr"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.nameFr || ""}
                  invalid={
                    validation.touched.nameFr && validation.errors.nameFr
                      ? true
                      : false
                  }
                />
                {validation.touched.nameFr && validation.errors.nameFr ? (
                  <FormFeedback type="invalid">
                    {validation.errors.nameFr}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label" htmlFor="nameAr">
                  {t("register.nameAr")} *
                </Label>
                <Input
                  name="nameAr"
                  className="form-control"
                  placeholder={t("register.enterNameAr")}
                  type="text"
                  id="nameAr"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.nameAr || ""}
                  invalid={
                    validation.touched.nameAr && validation.errors.nameAr
                      ? true
                      : false
                  }
                />
                {validation.touched.nameAr && validation.errors.nameAr ? (
                  <FormFeedback type="invalid">
                    {validation.errors.nameAr}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
          </Row>
          {/* Deuxième ligne : Catégorie et Description */}
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label" htmlFor="city">
                  {t("register.city")}
                  {"*"}
                </Label>
                <Input
                  name="city"
                  className="form-control"
                  placeholder={t("register.enterCity")}
                  type="text"
                  id="city"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.city || ""}
                  invalid={
                    validation.touched.city && validation.errors.city
                      ? true
                      : false
                  }
                />
                {validation.touched.city && validation.errors.city ? (
                  <FormFeedback type="invalid">
                    {validation.errors.city}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label" htmlFor="category">
                  {t("register.category")}
                  {"*"}
                </Label>
                <Input
                  name="category"
                  className="form-control"
                  type="select"
                  id="category"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.category || ""}
                  invalid={
                    validation.touched.category && validation.errors.category
                      ? true
                      : false
                  }
                >
                  <option value="Amateur">{t("register.amateur")}</option>
                  <option value="Semi-pro">{t("register.semiPro")}</option>
                  <option value="Professional">
                    {t("register.professional")}
                  </option>
                </Input>
                {validation.touched.category && validation.errors.category ? (
                  <FormFeedback type="invalid">
                    {validation.errors.category}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label" htmlFor="description">
                  {t("register.description")}
                </Label>
                <Input
                  name="description"
                  className="form-control"
                  placeholder={t("register.enterDescription")}
                  type="textarea"
                  id="description"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.description || ""}
                  invalid={
                    validation.touched.description &&
                    validation.errors.description
                      ? true
                      : false
                  }
                />
                {validation.touched.description &&
                validation.errors.description ? (
                  <FormFeedback type="invalid">
                    {validation.errors.description}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
          </Row>

          {/* Troisième ligne : Logo */}
          <Row>
            <Col md={12}>
              <div className="mb-3">
                <Label className="form-label" htmlFor="logo">
                  {t("register.logo")}
                  {"*"}
                </Label>
                <Input
                  name="logo"
                  className="form-control"
                  type="file"
                  id="logo"
                  onChange={(e) => {
                    const file = e.target.files[0]
                    if (file) {
                      validation.setFieldValue("logo", file)
                      validation.validateField("logo")
                    }
                  }}
                  onBlur={validation.handleBlur}
                  invalid={
                    validation.touched.logo && validation.errors.logo
                      ? true
                      : false
                  }
                />
                {validation.touched.logo && validation.errors.logo ? (
                  <FormFeedback type="invalid">
                    {validation.errors.logo}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card className="mb-4">
        <CardBody>
          <h5
            style={{
              fontWeight: "bold",
              fontSize: "25px",
              marginBottom: "30px",
            }}
          >
            {t("register.title2")}
          </h5>
          <Row>
            {/* Première ligne : Nom de Responsable et Adresse Email */}
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label" htmlFor="managerName">
                  {t("register.managerName")}
                  {"*"}
                </Label>
                <Input
                  name="managerName"
                  className="form-control"
                  placeholder={t("register.enterManagerName")}
                  type="text"
                  id="managerName"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.managerName || ""}
                  invalid={
                    validation.touched.managerName &&
                    validation.errors.managerName
                      ? true
                      : false
                  }
                />
                {validation.touched.managerName &&
                validation.errors.managerName ? (
                  <FormFeedback type="invalid">
                    {validation.errors.managerName}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label" htmlFor="managerPhoneNumber">
                  {t("register.managerPhoneNumber")}
                  {"*"}
                </Label>
                <Input
                  name="managerPhoneNumber"
                  autoComplete="off"
                  placeholder={t("register.enterManagerPhoneNumber")}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.managerPhoneNumber || ""}
                  invalid={
                    validation.touched.managerPhoneNumber &&
                    validation.errors.managerPhoneNumber
                      ? true
                      : false
                  }
                />
                {validation.touched.managerPhoneNumber &&
                validation.errors.managerPhoneNumber ? (
                  <FormFeedback type="invalid">
                    {validation.errors.managerPhoneNumber}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
          </Row>

          {/* Deuxième ligne : Fonction et Numéro de Téléphone */}
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label" htmlFor="managerRole">
                  {t("register.managerRole")}
                  {"*"}
                </Label>
                <Input
                  name="managerRole"
                  className="form-control"
                  type="select"
                  id="managerRole"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.managerRole || ""}
                  invalid={
                    validation.touched.managerRole &&
                    validation.errors.managerRole
                      ? true
                      : false
                  }
                >
                  <option value="President">{t("register.president")}</option>
                  <option value="Manager">{t("register.manager")}</option>
                  <option value="Recruiter">{t("register.recruiter")}</option>
                  <option value="Other">{t("register.other")}</option>
                </Input>
                {validation.touched.managerRole &&
                validation.errors.managerRole ? (
                  <FormFeedback type="invalid">
                    {validation.errors.managerRole}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label" htmlFor="managerEmail">
                  {t("register.managerEmail")}
                  {"*"}
                </Label>
                <Input
                  // autoComplete="off"
                  name="managerEmail"
                  className="form-control"
                  placeholder={t("register.enterManagerEmail")}
                  type="email"
                  id="managerEmail"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.managerEmail || ""}
                  invalid={
                    validation.touched.managerEmail &&
                    validation.errors.managerEmail
                      ? true
                      : false
                  }
                />
                {validation.touched.managerEmail &&
                validation.errors.managerEmail ? (
                  <FormFeedback type="invalid">
                    {validation.errors.managerEmail}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
          </Row>

          <Row>
            {validation.values.managerRole === "Other" && (
              <div className="mb-3">
                <Label className="form-label" htmlFor="otherRole">
                  {t("register.otherRole")} {/* Traduction pour "Autre rôle" */}
                </Label>
                <Input
                  name="otherRole"
                  className="form-control"
                  placeholder={t("register.otherRole")}
                  type="text"
                  id="otherRole"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.otherRole || ""}
                  invalid={
                    validation.touched.otherRole && validation.errors.otherRole
                      ? true
                      : false
                  }
                />
                {validation.touched.otherRole && validation.errors.otherRole ? (
                  <FormFeedback type="invalid">
                    {validation.errors.otherRole}
                  </FormFeedback>
                ) : null}
              </div>
            )}
          </Row>
        </CardBody>
      </Card>

      <Card className="mb-4">
        <CardBody>
          {/* Première ligne : Password et Confirm Password */}
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label" htmlFor="userpassword">
                  {t("register.password")}
                  {"*"}
                  {/* Translation key for Password */}
                </Label>
                <Input
                  autoComplete="new-password"
                  name="password"
                  type="password"
                  value={validation.values.password || ""}
                  placeholder={t("register.enterPassword")}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    validation.touched.password && validation.errors.password
                      ? true
                      : false
                  }
                />
                {validation.touched.password && validation.errors.password ? (
                  <FormFeedback type="invalid">
                    {validation.errors.password}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label" htmlFor="confirmPassword">
                  {t("register.confirmPassword")}
                  {"*"}
                </Label>
                <Input
                  name="confirmPassword"
                  value={validation.values.confirmPassword || ""}
                  type="password"
                  className="form-control"
                  onChange={validation.handleChange}
                  placeholder={t("register.confirmPassword")}
                  onBlur={validation.handleBlur}
                  invalid={
                    validation.touched.confirmPassword &&
                    validation.errors.confirmPassword
                      ? true
                      : false
                  }
                />
                {validation.touched.confirmPassword &&
                validation.errors.confirmPassword ? (
                  <FormFeedback type="invalid">
                    {validation.errors.confirmPassword}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
          </Row>

          {/* Deuxième ligne : Source */}
          <Row>
            <Col md={12}>
              <div className="mb-3">
                <Label className="form-label" htmlFor="source">
                  {t("register.sourceLabel")}
                  {"*"}
                </Label>
                <Input
                  name="source"
                  className="form-control"
                  type="select"
                  id="source"
                  onChange={handleSourceChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.source || ""}
                  invalid={
                    validation.touched.source && validation.errors.source
                      ? true
                      : false
                  }
                >
                  {Object.entries(SourceEnum).map(([key, value]) => (
                    <option key={key}>{value}</option>
                  ))}
                </Input>
                {validation.touched.source && validation.errors.source && (
                  <FormFeedback type="invalid">
                    {validation.errors.source}
                  </FormFeedback>
                )}
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>
      <div className="mb-3 row">
        <div className="text-end">
          {loading ? (
            <Button
              color="primary"
              disabled
              className="w-md waves-effect waves-light"
            >
              <Spinner size="sm" className="me-2" />
              <span>{t("loading")}</span>
            </Button>
          ) : (
            <Button
              className="btn btn-primary w-md waves-effect waves-light"
              type="submit"
            >
              {t("register.signUp")}
            </Button>
          )}
        </div>
      </div>
    </Form>
  )
}

export default ClubRegister
